import React, { useState, useRef } from 'react';
import { Box, Button, TextField, Typography, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar } from '@mui/material';

export const PasswordReset: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      // Replace with your backend endpoint for password reset request
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/password-reset-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Failed to send reset email');
      setSuccess(true);
      setSnackbar(true);
    } catch (err: any) {
      setError(err.message || 'Error sending reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 4, textAlign: 'center' }}>
      <Button variant="text" onClick={() => setOpen(true)} sx={{ mb: 2 }} aria-label="forgot password">
        Forgot password?
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth aria-labelledby="password-reset-title">
        <DialogTitle id="password-reset-title">Password Reset</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your email to receive a password reset link.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>}
          <Box component="form" onSubmit={handleRequest} aria-label="password reset form">
            <TextField
              inputRef={emailRef}
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              margin="normal"
              required
              autoComplete="email"
              aria-label="email"
              disabled={loading || success}
            />
            <DialogActions>
              <Button onClick={() => setOpen(false)} disabled={loading}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading || success} aria-label="send reset link">
                {loading ? <CircularProgress size={20} /> : 'Send Reset Link'}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(false)}
        message="Reset link sent! Check your email."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
